import { useCallback, useEffect, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { setCaseClaimed as requestSetCaseClaimed } from '@/api/cases';
import { useUserProfile } from '@/states/user-profile';
import { useGradeFlow } from '@/states/grade-flow';

import { useNavigateFlow } from './use-navigate-flow';
import { useRefreshCaseForGrade } from '../api/use-get-case-for-grade';

export const useClaimCase = () => {
  const [isClaiming, setIsClaiming] = useState(false);
  const [isClaimedByOtherUser, setIsClaimByOtherUser] = useState(false);

  const profile = useUserProfile((state) => ({ email: state.profile.email }));
  const { navigateFlow } = useNavigateFlow();

  const { _case, isCaseValidClaimed, setCaseClaimed, setFlowBlocked } = useGradeFlow(
    useShallow((s) => ({
      _case: s.case,
      isCaseValidClaimed: s.isCaseValidClaimed,
      setCaseClaimed: s.setCaseValidClaimed,
      setFlowBlocked: s.setFlowBlocked,
    }))
  );

  const { refresh } = useRefreshCaseForGrade(_case?.case_id);

  const handleClaimCase = useCallback(async () => {
    // we are only updating the claimed status when true, we are not unclaiming at this point
    if (!_case || !profile.email) return;

    try {
      setIsClaiming(true);
      await requestSetCaseClaimed(_case.case_id, profile.email);

      setCaseClaimed(true);
      setIsClaiming(false);
      void refresh();
      navigateFlow('GRADING_CASE_STEP_2');
    } catch (error) {
      console.error('Error setting case claimed', error);
      setIsClaiming(false);
    }
  }, [_case, navigateFlow, profile.email, setCaseClaimed]);

  // this effect will help to identify if the case is claimed
  useEffect(() => {
    const currentGrade = _case?.grades?.sort((a, b) => {
      return new Date(b.createdUtc).getTime() - new Date(a.createdUtc).getTime();
    })[0];

    // block the step if we don't have a case or if it's already grade
    if (Boolean(currentGrade) || !_case) {
      return;
    }

    // if not claimed let the user decide if wants to claim it
    if (!_case.claimed_time) {
      return;
    }

    if (isCaseValidClaimed) {
      // the only way to get here is if the user go back to this step, we let the use know that the case is already claimed
      return;
    }

    // if the case is claimed, we need to check if the user is the one that claimed it
    const claimedDate = new Date(_case.claimed_time);
    const futureDate = new Date(claimedDate.getTime() + 30 * 60 * 1000); // 30 minutes in the future

    // if the time has expired, let the user claim it, no matter who claimed it
    if (futureDate < new Date()) return;

    // here the claim status is not expired
    // if the user is the one that claimed it, let the user know
    if (_case?.claimed_by === profile.email) {
      setCaseClaimed(true);
      navigateFlow('GRADING_CASE_STEP_2');
    } else {
      // if the user did not claim it, let the user know when it will be available
      // for this we are using a banner outside of this component and also disabling the submit button
      setIsClaimByOtherUser(true);
    }
  }, [isCaseValidClaimed, _case, navigateFlow, profile.email, setCaseClaimed, setFlowBlocked]);

  return { isClaimedByOtherUser, isClaiming, handleClaimCase };
};
